"use client";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { MailOpen, Menu, PhoneCall } from "lucide-react";
import useLocalizer from "@/hooks/useLocalizer";
import { Link } from "@/i18n/routing";
import Image from "next/image";
import { Separator } from "@/components/ui/separator";
import SubMenu from "./sidebarMenu";
import { cn } from "@/lib/utils/utils";
import InlineTitle from "@/components/shared/inlineTitle";
import { FaFacebook, FaInstagram, FaTwitter, FaYoutube } from "react-icons/fa";
import Flex from "@/components/core/flex";
import Text from "@/components/shared/text";
import HeaderLink from "../header/headerLink";
import { SocialMedia } from "@/types/info";
import DynamicIcon from "@/components/shared/dynamicIcon";

interface SidebarProps {
  className?: string;
  email?: string;
  phone?: string;
  routes: {
    title: string;
    href: string;
    items?: {
      title: string;
      href: string;
    }[];
  }[];
  logo?: string;
  socialMedia?: SocialMedia[];
}

const Sidebar: React.FC<SidebarProps> = ({
  className,
  email,
  phone,
  routes,
  logo,
  socialMedia,
}) => {
  const { isRtl, t } = useLocalizer();

  return (
    <div className={cn("lg:hidden flex items-center p-2", className)}>
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="icon" aria-label="Menu">
            <Menu className="size-6 font-bold!" />
          </Button>
        </SheetTrigger>
        <SheetContent
          className="bg-secondary border-none z-99999"
          side={isRtl ? "right" : "left"}
        >
          <SheetHeader className="flex flex-col gap-4">
            <SheetTitle />
            <SheetDescription>
              {logo && (
                <Link href="/">
                  <Image
                    src={logo}
                    alt="logo"
                    height={55}
                    width={110}
                    priority
                    placeholder="empty"
                  />
                </Link>
              )}
            </SheetDescription>
            <Separator className="bg-primary/20" />
          </SheetHeader>
          <SubMenu items={routes} />
          <Flex direction="col" spacing={{ all: 2 }} className="px-4">
            <InlineTitle
              icon={<MailOpen size={16} />}
              value={
                <Link href={`mailto:${email}`}>
                  <Text as="span" color="white">
                    {email}
                  </Text>
                </Link>
              }
              className="hover:text-primary"
            />
            <InlineTitle
              icon={<PhoneCall size={16} />}
              value={
                <Link href={`tel:${phone}`}>
                  <Text bdi as="span" color="white">
                    {phone}
                  </Text>
                </Link>
              }
              className="hover:text-primary"
            />

            {socialMedia && (
              <Flex
                direction="row"
                spacing={{ all: 3 }}
                flexWrap="wrap"
                className="text-white my-5"
              >
                {socialMedia?.map((social, index) => (
                  <HeaderLink
                    key={index}
                    title={isRtl ? social.arLinkTitle : social.enLinkTitle}
                    href={social.linkHref ?? "#"}
                    icon={<DynamicIcon name={social?.icon?.code ?? ""} />}
                  />
                ))}
              </Flex>
            )}
          </Flex>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Sidebar;
